import React, { useEffect, useState, useCallback, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";

/* ═══════════════════════════════════════════════════════════════════════════
   NEOPASS — AWWWARDS HERO SECTION
   Animation logic preserved. Visual system rebuilt from scratch.
   ═══════════════════════════════════════════════════════════════════════════ */

const TYPE_SPEED = 20;
const SCENE_END_HOLD = 2000;

const t = (v) => Math.round(v);

const CODE_ANSWER = `function twoSum(nums, target) {
  const map = new Map();
  for (let i = 0; i < nums.length; i++) {
    const complement = target - nums[i];
    if (map.has(complement)) return [map.get(complement), i];
    map.set(nums[i], i);
  }
  return [];
}`;

const SCENES = [
  { id: "ctrl-q", topic: "Solve MCQ on any page", keys: ["Ctrl", "Q"] },
  { id: "alt-shift-a", topic: "Automark MCQ with AI", keys: ["Alt", "Shift", "A"] },
  { id: "alt-shift-t", topic: "Autotype coding questions", keys: ["Alt", "Shift", "T"] },
  { id: "alt-shift-q", topic: "Batch solve MCQs", keys: ["Alt", "Shift", "Q"] },
  { id: "right-click-solve", topic: "Solve entire page", keys: null },
  { id: "netacad-autosolve", topic: "Auto-solve NetAcad MCQs", keys: ["Mouse"], control: "mouse" },
  { id: "screenshare-bypass", topic: "Screen share bypassed", keys: null },
];

/* ── DESIGN TOKENS ── */
const T = {
  bg: "#060608",
  panel: "#0C0C10",
  surface: "#111116",
  surfaceHi: "#18181F",
  border: "rgba(255,255,255,0.06)",
  borderMid: "rgba(255,255,255,0.10)",
  borderHi: "rgba(255,255,255,0.16)",
  text: "#F0F0F5",
  muted: "rgba(240,240,245,0.5)",
  dim: "rgba(240,240,245,0.25)",
    // Accent — app yellow
    lime: "hsl(var(--accent-color))",
    limeDim: "hsla(var(--accent-color), 0.12)",
    limeGlow: "hsla(var(--accent-color), 0.25)",
  // Secondary — cool violet
  violet: "#7C6EFA",
  violetDim: "rgba(124,110,250,0.15)",
  // Semantic
  emerald: "#34D399",
  emeraldDim: "rgba(52,211,153,0.14)",
  red: "#F87171",
  sliderco: "rgba(255, 255, 255, 0.09)"
};

/* ── GOOGLE FONTS ── */
const FONTS = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=IBM+Plex+Mono:wght@400;500;600&family=Instrument+Sans:wght@400;500;600&display=swap');
`;

/* ═══════════════════════════════════════════════════════════════════════════
   SHARED MICRO-COMPONENTS
   ═══════════════════════════════════════════════════════════════════════════ */

function Cursor({ variant = "default" }) {
  if (variant === "text") {
    return (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none"
        style={{ display: "block", filter: `drop-shadow(0 0 6px ${T.lime})` }}>
        <rect x="10" y="1" width="2" height="20" fill={T.lime} rx="1" />
        <rect x="6" y="1" width="10" height="2" fill={T.lime} rx="1" />
        <rect x="6" y="19" width="10" height="2" fill={T.lime} rx="1" />
      </svg>
    );
  }
  return (
    <svg width="22" height="26" viewBox="0 0 22 26" fill="none"
      style={{ display: "block", filter: "drop-shadow(0 4px 10px rgba(0,0,0,0.7))" }}>
      <path d="M2 2L2 20L7 15L10.5 23L13.2 21.8L9.8 14H17L2 2Z"
        fill="#FFFFFF" stroke="#000" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

function KeyCap({ label, pressed, delay = 0 }) {
  return (
    <motion.div
      initial={{ y: 0, opacity: 0, scale: 0.85 }}
      animate={{
        y: pressed ? 3 : 0,
        opacity: 1,
        scale: 1,
        background: pressed
          ? T.text
          : "linear-gradient(180deg, rgba(255,255,255,0.09) 0%, rgba(255,255,255,0.04) 100%)",
        color: pressed ? "#000" : T.text,
        borderColor: pressed ? T.text : T.borderMid,
        boxShadow: pressed
          ? `0 0 12px ${T.limeGlow}`
          : `0 3px 0 rgba(255,255,255,0.08), 0 6px 16px rgba(0,0,0,0.4)`,
      }}
      transition={{ delay, type: "spring", stiffness: 450, damping: 22 }}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: label.length > 3 ? 58 : label.length > 1 ? 46 : 34,
        height: 34,
        padding: "0 10px",
        borderRadius: 7,
        border: `1px solid ${T.borderMid}`,
        fontSize: 11,
        fontWeight: 600,
        fontFamily: "'IBM Plex Mono', monospace",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        userSelect: "none",
      }}
    >{label}</motion.div>
  );
}

function KeyCombo({ keys, pressed }) {
  return (
    <motion.div style={{ display: "flex", gap: 6, alignItems: "center" }}
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}>
      {keys.map((k, i) => (
        <React.Fragment key={k}>
          {i > 0 && <span style={{ color: T.dim, fontSize: 12 }}>+</span>}
          <KeyCap label={k} pressed={pressed} delay={pressed ? i * 0.04 : 0} />
        </React.Fragment>
      ))}
    </motion.div>
  );
}

function MouseKey({ pressed }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
    >
      <KeyCap label="Mouse" pressed={pressed} />
      <div style={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Cursor />
      </div>
    </motion.div>
  );
}

function BlinkCursor() {
  return (
    <motion.span
      animate={{ opacity: [1, 0] }}
      transition={{ repeat: Infinity, duration: 0.75, ease: "steps(2)" }}
      style={{
        display: "inline-block", width: 5, height: 15, background: T.lime,
        marginLeft: 2, verticalAlign: "middle", borderRadius: 1
      }}
    />
  );
}

function SelectableText({ text, isSelected, progress = 1, style = {} }) {
  const chars = text.split("");
  const n = Math.floor(chars.length * progress);
  return (
    <span style={{ fontSize: 14, lineHeight: 1.65, color: T.text, ...style }}>
      {chars.map((c, i) => (
        <span key={i} style={{
          background: isSelected && i < n ? T.limeGlow : "transparent",
          color: isSelected && i < n ? T.lime : "inherit",
          transition: "background 0.04s, color 0.04s",
          borderRadius: 2,
        }}>{c}</span>
      ))}
    </span>
  );
}

/* ── Window chrome ── */
const Win = ({ children, url, glowing }) => (
  <motion.div
    animate={{ borderColor: glowing ? T.limeGlow : T.border }}
    style={{
      background: T.surface,
      border: `1px solid ${T.border}`,
      borderRadius: 14,
      overflow: "hidden",
      boxShadow: `0 20px 50px rgba(0,0,0,0.55)`,
    }}
  >
    <div style={{
      background: T.surfaceHi,
      borderBottom: `1px solid ${T.border}`,
      padding: "10px 16px",
      display: "flex", alignItems: "center", justifyContent: "space-between",
    }}>
      <div style={{ display: "flex", gap: 6 }}>
        {["#EF4444", "#F59E0B", "#22C55E"].map((c, i) => (
          <span key={i} style={{ width: 9, height: 9, borderRadius: "50%", background: c, opacity: 0.85 }} />
        ))}
      </div>
      <span style={{ fontSize: 11, color: T.dim, fontFamily: "'IBM Plex Mono',monospace", letterSpacing: "0.03em" }}>
        {url}
      </span>
    </div>
    {children}
  </motion.div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   SCENE 1 — CTRL+Q : ASK AI
   ═══════════════════════════════════════════════════════════════════════════ */
function SceneAskAI({ phase }) {
  const questionText = "Which approach is most optimal for finding a cycle in a directed graph?";
  const options = [
    "Breadth-First Search (BFS) with a queue",
    "Depth-First Search (DFS) with a recursion stack",
    "Dijkstra's Shortest Path algorithm",
    "Kruskal's Minimum Spanning Tree algorithm",
  ];

  const [selProg, setSelProg] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) clearInterval(ref.current);
    if (phase === "idle") { setSelProg(0); return; }
    if (phase === "selecting") {
      let p = 0;
      ref.current = setInterval(() => {
        p += 0.014; setSelProg(Math.min(p, 1));
        if (p >= 1) clearInterval(ref.current);
      }, 16);
    } else setSelProg(1);
    return () => clearInterval(ref.current);
  }, [phase]);

  const qP = p => Math.min(Math.max(p / 0.3, 0), 1);
  const oP = (p, i) => { const s = 0.3 + i * 0.175, e = s + 0.175; return p <= s ? 0 : p >= e ? 1 : (p - s) / 0.175; };
  const curX = phase === "idle" ? 360 : (phase === "selecting" || phase === "selected" || phase === "pressed" || phase === "action")
    ? 20 + (320) * (phase === "selecting" ? selProg : 1) : 360;
  const curY = phase === "idle" ? 100 : 22 + (190) * (phase === "selecting" ? selProg : 1);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, height: "100%" }}>
      <Win url="exam.platform/q/14">
        <div style={{ padding: 20, position: "relative" }}>
          <div style={{ marginBottom: 18 }}>
            <SelectableText text={questionText} isSelected={phase !== "idle"}
              progress={qP(selProg)} style={{ fontSize: 15, fontWeight: 500 }} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {options.map((opt, i) => (
              <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                <span style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  width: 22, height: 22, borderRadius: "50%",
                  border: `1px solid ${T.borderMid}`, fontSize: 11, color: T.muted,
                  fontFamily: "'IBM Plex Mono',monospace", flexShrink: 0, marginTop: 1,
                }}>{String.fromCharCode(65 + i)}</span>
                <SelectableText text={opt} isSelected={phase !== "idle"} progress={oP(selProg, i)} />
              </div>
            ))}
          </div>

          {/* Cursor */}
          {(phase === "idle" || phase === "selecting" || phase === "selected") && (
            <motion.div animate={{ x: curX, y: curY }} transition={{ type: "spring", stiffness: 100, damping: 18 }}
              style={{ position: "absolute", top: 0, left: 0, zIndex: 50, pointerEvents: "none" }}>
              <Cursor variant={phase === "selecting" ? "text" : "default"} />
            </motion.div>
          )}
        </div>
      </Win>

      {/* Answer pill */}
      <AnimatePresence>
        {phase === "action" && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8 }}
            style={{
              alignSelf: "flex-start",
              background: `linear-gradient(135deg, rgba(12,12,16,0.97), rgba(8,8,12,0.97))`,
              border: `1px solid ${T.limeGlow}`,
              borderRadius: 12,
              padding: "13px 20px",
              boxShadow: `0 16px 40px rgba(0,0,0,0.7), 0 0 20px ${T.limeDim}`,
              display: "flex", alignItems: "center", gap: 12,
            }}
          >
            <span style={{
              width: 6, height: 6, borderRadius: "50%", background: T.lime, flexShrink: 0,
              boxShadow: `0 0 8px ${T.lime}`
            }} />
            <span style={{ fontSize: 13, color: T.text, fontFamily: "'Instrument Sans',sans-serif" }}>
              <span style={{ color: T.lime, fontWeight: 700, marginRight: 6 }}>B.</span>
              Depth-First Search (DFS) with a recursion stack
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SCENE 2 — ALT+SHIFT+A : AUTOMARK
   ═══════════════════════════════════════════════════════════════════════════ */
function SceneInstantSolve({ phase }) {
  const opts = [
    { id: "A", text: "React DOM" },
    { id: "B", text: "Virtual DOM", correct: true },
    { id: "C", text: "Shadow DOM" },
    { id: "D", text: "Document Fragment" },
  ];
  const done = phase === "action";
  return (
    <Win url="app.examly.net/test">
      <div style={{ padding: 20 }}>
        <p style={{ margin: "0 0 20px", fontSize: 14, lineHeight: 1.6, color: T.text, fontWeight: 500 }}>
          What mechanism does React use to optimise rendering performance?
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {opts.map(opt => {
            const ok = done && opt.correct;
            return (
              <motion.div key={opt.id}
                animate={{
                  background: ok ? T.emeraldDim : T.surfaceHi,
                  borderColor: ok ? T.emerald : T.borderMid,
                }}
                style={{
                  display: "flex", alignItems: "center", gap: 14, padding: "13px 16px",
                  borderRadius: 10, border: `1px solid ${T.borderMid}`
                }}>
                <motion.div animate={{
                  background: ok ? T.emerald : "transparent",
                  color: ok ? "#000" : T.muted,
                  borderColor: ok ? T.emerald : T.borderMid,
                }} style={{
                  width: 26, height: 26, borderRadius: "50%", border: `1px solid`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 11, fontWeight: 700, fontFamily: "'IBM Plex Mono',monospace", flexShrink: 0
                }}>
                  {ok ? "✓" : opt.id}
                </motion.div>
                <span style={{ fontSize: 13, color: ok ? T.text : T.muted }}>{opt.text}</span>
                {ok && (
                  <motion.span initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                    style={{
                      marginLeft: "auto", fontSize: 10, color: T.emerald, fontWeight: 600,
                      letterSpacing: "0.08em", textTransform: "uppercase",
                      fontFamily: "'IBM Plex Mono',monospace"
                    }}>
                    Auto-marked
                  </motion.span>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </Win>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SCENE 3 — ALT+SHIFT+T : AUTOTYPE
   ═══════════════════════════════════════════════════════════════════════════ */
function SceneAutoType({ phase }) {
  const [typed, setTyped] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) clearInterval(ref.current);
    setTyped("");
    if (phase === "action") {
      let i = 0;
      ref.current = setInterval(() => {
        i++; setTyped(CODE_ANSWER.slice(0, i));
        if (i >= CODE_ANSWER.length) clearInterval(ref.current);
      }, TYPE_SPEED);
    }
    return () => clearInterval(ref.current);
  }, [phase]);
  const typing = phase === "action" && typed.length < CODE_ANSWER.length;

  const escapeHtml = (s) => s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // Simple syntax colouring
  const coloured = (src) => {
    const safe = escapeHtml(src);
    return safe
      .replace(/(".*?")/g, `<span style="color:#34D399">$1</span>`)
      .replace(/(\btwoSum\b)/g, `<span style="color:#CBFF4D">$1</span>`)
      .replace(/(function|return|const|for|let|if|new|Map)/g, `<span style="color:#7C6EFA">$1</span>`)
      .replace(/(\/\/.*)/g, `<span style="color:rgba(240,240,245,0.35)">$1</span>`);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, height: "100%" }}>
      <div style={{
        padding: "12px 16px",
        background: T.surfaceHi, border: `1px solid ${T.border}`, borderRadius: 10
      }}>
        <p style={{ margin: 0, fontSize: 13, color: T.muted, lineHeight: 1.55 }}>
          Write a function{" "}
          <code style={{
            background: T.limeDim, color: T.lime, padding: "1px 6px", borderRadius: 4,
            fontFamily: "'IBM Plex Mono',monospace", fontSize: 12
          }}>twoSum(nums, target)</code>
          {" "}that returns indices of two numbers summing to the target.
        </p>
      </div>
      <motion.div style={{ flex: 1 }} animate={{ borderColor: typing ? T.limeGlow : T.border }}>
        <Win url="solution.js" glowing={typing}>
          <div style={{ padding: "16px 20px", minHeight: 200, position: "relative" }}>
            {typing && (
              <motion.div animate={{ opacity: [0.5, 1, 0.5] }} transition={{ repeat: Infinity, duration: 1.2 }}
                style={{
                  position: "absolute", top: 10, right: 12, display: "flex", alignItems: "center",
                  gap: 6, fontSize: 10, color: T.lime, fontFamily: "'IBM Plex Mono',monospace",
                  letterSpacing: "0.08em"
                }}>
                <motion.span animate={{ scale: [1, 1.2, 1] }} transition={{ repeat: Infinity, duration: 0.6 }}
                  style={{ width: 6, height: 6, borderRadius: "50%", background: T.lime }} />
                TYPING
              </motion.div>
            )}
            <pre style={{ margin: 0, overflow: "hidden" }}>
              <code style={{
                fontFamily: "'IBM Plex Mono',monospace", fontSize: 12.5, lineHeight: 1.65,
                color: "rgba(180,185,220,0.9)"
              }}
                dangerouslySetInnerHTML={{
                  __html: coloured(typed || "// Awaiting Alt+Shift+T…")
                }} />
              {typing && <BlinkCursor />}
            </pre>
          </div>
        </Win>
      </motion.div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SCENE 4 — ALT+SHIFT+Q : BATCH SOLVE
   ═══════════════════════════════════════════════════════════════════════════ */
function SceneBatchSolve({ phase }) {
  const qs = [
    { q: "Average time complexity of Quicksort?", a: "O(n log n)" },
    { q: "Space complexity of Merge Sort?", a: "O(n)" },
    { q: "Which data structure uses LIFO?", a: "Stack" },
    { q: "Worst-case time of BST search?", a: "O(n)" },
  ];
  const done = phase === "action";
  return (
    <Win url="examly.net/batch">
      <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 20 }}>
        {qs.map((item, i) => (
          <motion.div key={i}
            animate={{
              borderColor: done ? T.emerald : T.borderMid,
              background: done ? T.emeraldDim : T.surfaceHi,
            }}
            transition={{ delay: done ? i * 0.12 : 0, type: "spring" }}
            style={{
              padding: "13px 16px", borderRadius: 10, border: `1px solid ${T.borderMid}`,
              display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12
            }}>
            <span style={{ fontSize: 13, color: T.text }}>
              <span style={{
                color: T.dim, marginRight: 8, fontFamily: "'IBM Plex Mono',monospace",
                fontSize: 11
              }}>{i + 1}.</span>
              {item.q}
            </span>
            <AnimatePresence>
              {done && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.6, x: 16 }}
                  animate={{ opacity: 1, scale: 1, x: 0 }}
                  transition={{ type: "spring", delay: i * 0.12 + 0.08 }}
                  style={{
                    background: T.emerald, color: "#000", padding: "4px 10px",
                    borderRadius: 6, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap",
                    fontFamily: "'IBM Plex Mono',monospace"
                  }}>
                  {item.a} ✓
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </div>
    </Win>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SCENE 5 — RIGHT CLICK : FULL PAGE
   ═══════════════════════════════════════════════════════════════════════════ */
function SceneFullPage({ phase }) {
  const isMenu = ["menu", "menu-hover", "scanning"].includes(phase);
  const isHover = ["menu-hover", "scanning"].includes(phase);
  const isScan = phase === "scanning";
  const done = phase === "action";
  const qs = [
    { q: "What is the primary key of a relational database?", a: "Unique ID" },
    { q: "Which protocol is used for secure communication?", a: "HTTPS" },
    { q: "What concept restricts direct access to object components?", a: "Encapsulation" },
    { q: "Average time complexity of hash table search?", a: "O(1)" },
  ];
  const curPos = {
    idle: { x: 260, y: 160 }, move: { x: 110, y: 52 }, menu: { x: 110, y: 52 },
    "menu-hover": { x: 150, y: 183 }, scanning: { x: 150, y: 183 }, action: { x: 150, y: 183 },
  }[phase] || { x: 260, y: 160 };

  return (
    <div style={{ position: "relative", height: "100%" }}>
      <div style={{
        opacity: isMenu || isScan ? 0.25 : 1, transition: "opacity 0.4s",
        filter: isMenu || isScan ? "blur(1.5px)" : "none"
      }}>
        {qs.map((item, i) => (
          <motion.div key={i}
            animate={{
              borderColor: done ? T.emerald : T.borderMid,
              background: done ? T.emeraldDim : T.surfaceHi
            }}
            transition={{ delay: done ? i * 0.09 : 0 }}
            style={{
              padding: "12px 16px", border: `1px solid ${T.borderMid}`,
              borderRadius: 9, marginBottom: 10,
              display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12
            }}>
            <span style={{ fontSize: 13, color: T.text }}>
              <span style={{
                color: T.dim, marginRight: 6, fontFamily: "'IBM Plex Mono',monospace",
                fontSize: 11
              }}>{i + 1}.</span>
              {item.q}
            </span>
            {done && (
              <motion.div initial={{ opacity: 0, scale: 0.6, x: 12 }} animate={{ opacity: 1, scale: 1, x: 0 }}
                transition={{ type: "spring", delay: i * 0.09 + 0.06 }}
                style={{
                  background: T.emerald, color: "#000", padding: "3px 9px",
                  borderRadius: 5, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap",
                  fontFamily: "'IBM Plex Mono',monospace"
                }}>
                {item.a} ✓
              </motion.div>
            )}
          </motion.div>
        ))}
      </div>

      {/* Context Menu */}
      <AnimatePresence>
        {isMenu && (
          <motion.div initial={{ opacity: 0, scale: 0.92, y: -8 }} animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            style={{
              position: "absolute", top: 50, left: 110, width: 210,
              background: "rgba(16,16,22,0.97)",
              border: `1px solid ${T.borderMid}`, borderRadius: 12, padding: 6,
              boxShadow: "0 24px 48px rgba(0,0,0,0.85)", backdropFilter: "blur(12px)", zIndex: 20
            }}>
            {["Back", "Forward", "Reload", "—", "Solve page with AI", "Inspect"].map((item, i) => {
              if (item === "—") return <div key={i} style={{ height: 1, background: T.border, margin: "4px 0" }} />;
              const isSolve = item === "Solve page with AI";
              const active = isSolve && isHover;
              return (
                <motion.div key={i}
                  animate={{
                    background: active ? T.violetDim : "transparent",
                    color: active ? T.violet : isSolve ? T.text : T.muted,
                  }}
                  style={{
                    padding: "7px 11px", borderRadius: 7, fontSize: 12,
                    fontWeight: isSolve ? 600 : 400,
                    display: "flex", alignItems: "center", gap: 8,
                    fontFamily: "'Instrument Sans',sans-serif"
                  }}>
                  {isSolve && <span style={{ color: T.violet, fontSize: 14 }}>◈</span>}
                  {item}
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Scanning overlay */}
      <AnimatePresence>
        {isScan && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{
              position: "absolute", inset: 0, display: "flex", alignItems: "center",
              justifyContent: "center", flexDirection: "column", gap: 14
            }}>
            <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1.8, ease: "linear" }}
              style={{
                width: 36, height: 36, borderRadius: "50%",
                border: `2px solid ${T.violetDim}`, borderTopColor: T.violet
              }} />
            <span style={{
              color: T.violet, fontWeight: 600, fontSize: 12,
              letterSpacing: "0.14em", fontFamily: "'IBM Plex Mono',monospace"
            }}>
              SCANNING PAGE
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cursor */}
      <motion.div animate={curPos} transition={{ type: "spring", stiffness: 180, damping: 22 }}
        style={{ position: "absolute", top: 0, left: 0, zIndex: 50, pointerEvents: "none" }}>
        <Cursor />
        {phase === "menu" && (
          <motion.div initial={{ scale: 0.4, opacity: 1 }} animate={{ scale: 2.4, opacity: 0 }}
            transition={{ duration: 0.45 }}
            style={{
              position: "absolute", top: -10, left: -10, width: 20, height: 20,
              borderRadius: "50%", border: `2px solid ${T.lime}`
            }} />
        )}
      </motion.div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SCENE 6 — SCREEN SHARE BYPASS / STEALTH
   ═══════════════════════════════════════════════════════════════════════════ */
function SceneStealth({ phase }) {
  const show = phase === "prompt" || phase === "action";
  const done = phase === "action";
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
      <AnimatePresence>
        {show && (
          <motion.div initial={{ opacity: 0, scale: 0.92, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }}
            style={{
              width: "100%", maxWidth: 380,
              background: T.panel,
              border: `1px solid ${T.borderMid}`,
              borderRadius: 16, padding: 24,
              boxShadow: `0 32px 64px rgba(0,0,0,0.65), 0 0 0 1px rgba(255,255,255,0.04)`
            }}>
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              <div style={{
                width: 38, height: 38, borderRadius: 9,
                background: "rgba(248,113,113,0.1)", display: "flex", alignItems: "center",
                justifyContent: "center", color: T.red, fontSize: 17, flexShrink: 0
              }}>⚠</div>
              <div>
                <p style={{
                  margin: 0, color: T.text, fontSize: 14, fontWeight: 600,
                  fontFamily: "'Syne',sans-serif"
                }}>Proctoring Alert</p>
                <p style={{ margin: 0, color: T.dim, fontSize: 12 }}>Screen sharing requested</p>
              </div>
            </div>
            {/* Options */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <motion.div
                animate={{
                  borderColor: done ? T.emerald : T.borderMid,
                  background: done ? T.emeraldDim : T.surfaceHi
                }}
                style={{
                  padding: "14px 16px", borderRadius: 11, border: `1px solid`,
                  display: "flex", justifyContent: "space-between", alignItems: "center"
                }}>
                <div>
                  <p style={{ margin: 0, color: T.text, fontWeight: 500, fontSize: 13 }}>Chrome Tab Only</p>
                  <p style={{ margin: 0, color: T.dim, fontSize: 11.5 }}>NeoPass remains hidden</p>
                </div>
                {done
                  ? <span style={{
                    fontSize: 11, color: T.emerald, fontWeight: 700,
                    fontFamily: "'IBM Plex Mono',monospace", letterSpacing: "0.06em"
                  }}>ACTIVE</span>
                  : <span style={{
                    width: 14, height: 14, borderRadius: 7,
                    border: `2px solid ${T.dim}`, display: "inline-block"
                  }} />}
              </motion.div>
              <div style={{
                padding: "14px 16px", borderRadius: 11,
                border: `1px solid ${T.border}`, background: T.surface,
                display: "flex", justifyContent: "space-between", alignItems: "center",
                opacity: done ? 0.38 : 1
              }}>
                <div>
                  <p style={{ margin: 0, color: T.text, fontWeight: 500, fontSize: 13 }}>Entire Screen</p>
                  <p style={{ margin: 0, color: T.red, fontSize: 11.5 }}>Exposes NeoPass — dangerous</p>
                </div>
                <span style={{
                  width: 14, height: 14, borderRadius: 7,
                  border: `2px solid ${T.dim}`, display: "inline-block"
                }} />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SCENE 7 — NETACAD AUTOSOLVE : MOUSE CLICK + AUTO-FILL
   ═══════════════════════════════════════════════════════════════════════════ */
function SceneNetAcadAutosolve({ phase }) {
  const options = [
    { id: "A", text: "FTP" },
    { id: "B", text: "HTTPS", correct: true },
    { id: "C", text: "Telnet" },
    { id: "D", text: "SNMP" },
  ];

  const pointerPos = {
    idle: { x: 328, y: 118 },
    move: { x: 180, y: 45 },
    hover: { x: 180, y: 45 },
    click: { x: 180, y: 45 },
    action: { x: 180, y: 45 },
  }[phase] || { x: 328, y: 118 };

  const showQuestionGlow = phase === "hover" || phase === "click" || phase === "action";
  const fillAnswer = phase === "action";

  return (
    <Win url="netacad.net/lab/42">
      <div style={{ padding: 20, position: "relative" }}>
        <div style={{ marginBottom: 18 }}>
          <div
            style={{
              padding: "14px 16px",
              borderRadius: 12,
              border: `1px solid ${showQuestionGlow ? T.violet : T.borderMid}`,
              background: showQuestionGlow ? T.violetDim : T.surfaceHi,
              boxShadow: showQuestionGlow ? `0 0 0 1px ${T.violetDim}` : "none",
            }}
          >
            <p style={{ margin: 0, color: T.text, fontSize: 14, lineHeight: 1.6, fontWeight: 500 }}>
              Which protocol is used to secure web traffic?
            </p>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {options.map((opt) => {
            const isCorrect = fillAnswer && opt.correct;
            return (
              <motion.div
                key={opt.id}
                animate={{
                  background: isCorrect ? T.emeraldDim : T.surfaceHi,
                  borderColor: isCorrect ? T.emerald : showQuestionGlow ? T.violet : T.borderMid,
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  padding: "13px 16px",
                  borderRadius: 10,
                  border: `1px solid ${T.borderMid}`,
                  position: "relative",
                }}
              >
                <motion.div
                  animate={{
                    background: isCorrect ? T.emerald : "transparent",
                    color: isCorrect ? "#000" : T.muted,
                    borderColor: isCorrect ? T.emerald : T.borderMid,
                  }}
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    border: "1px solid",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 700,
                    fontFamily: "'IBM Plex Mono',monospace",
                    flexShrink: 0,
                  }}
                >
                  {isCorrect ? "✓" : opt.id}
                </motion.div>
                <span style={{ fontSize: 13, color: isCorrect ? T.text : T.muted }}>{opt.text}</span>
                {isCorrect && (
                  <motion.span
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    style={{
                      marginLeft: "auto",
                      fontSize: 10,
                      color: T.emerald,
                      fontWeight: 600,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      fontFamily: "'IBM Plex Mono',monospace",
                    }}
                  >
                    Auto-filled
                  </motion.span>
                )}
              </motion.div>
            );
          })}
        </div>

        <motion.div
          animate={pointerPos}
          transition={{ type: "spring", stiffness: 180, damping: 20 }}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 50,
            pointerEvents: "none",
          }}
        >
          <Cursor />
          {phase === "click" && (
            <motion.div
              initial={{ scale: 0.35, opacity: 1 }}
              animate={{ scale: 2.3, opacity: 0 }}
              transition={{ duration: 0.42 }}
              style={{
                position: "absolute",
                top: -10,
                left: -10,
                width: 20,
                height: 20,
                borderRadius: "50%",
                border: `2px solid ${T.lime}`,
              }}
            />
          )}
          {(phase === "click" || phase === "action") && (
            <motion.div
              animate={{ opacity: [0.55, 1, 0.55] }}
              transition={{ repeat: Infinity, duration: 1.1 }}
              style={{
                position: "absolute",
                top: 34,
                left: 28,
                width: 128,
                height: 30,
                borderRadius: 12,
                background: phase === "action" ? T.emeraldDim : T.violetDim,
                border: `1px solid ${phase === "action" ? T.emerald : T.violet}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: phase === "action" ? T.emerald : T.violet,
                fontSize: 10,
                letterSpacing: "0.12em",
                fontFamily: "'IBM Plex Mono',monospace",
                textTransform: "uppercase",
              }}
            >
              Clicking question
            </motion.div>
          )}
        </motion.div>
      </div>
    </Win>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ANIMATION TIMELINE HOOK
   ═══════════════════════════════════════════════════════════════════════════ */
function useHeroTimeline() {
  const [sceneIdx, setSceneIdx] = useState(0);
  const [phase, setPhase] = useState("idle");
  const timerRef = useRef(null);
  const scene = SCENES[sceneIdx];

  const runTimeline = useCallback((idx) => {
    if (timerRef.current) timerRef.current.forEach(clearTimeout);
    const timers = [];
    const s = SCENES[idx];
    let endAt = 0;

    if (s.id === "ctrl-q") {
      setPhase("idle");
      timers.push(setTimeout(() => setPhase("selecting"), t(800)));
      timers.push(setTimeout(() => setPhase("selected"), t(2400)));
      timers.push(setTimeout(() => setPhase("pressed"), t(3000)));
      timers.push(setTimeout(() => setPhase("action"), t(3600)));
      endAt = t(7500);
    } else if (s.id === "alt-shift-q") {
      setPhase("idle");
      timers.push(setTimeout(() => setPhase("select"), t(800)));
      timers.push(setTimeout(() => setPhase("pressed"), t(2000)));
      timers.push(setTimeout(() => setPhase("action"), t(2600)));
      endAt = t(5000);
    } else if (s.id === "right-click-solve") {
      setPhase("idle");
      timers.push(setTimeout(() => setPhase("move"), t(400)));
      timers.push(setTimeout(() => setPhase("menu"), t(1200)));
      timers.push(setTimeout(() => setPhase("menu-hover"), t(2000)));
      timers.push(setTimeout(() => setPhase("scanning"), t(2800)));
      timers.push(setTimeout(() => setPhase("action"), t(4400)));
      endAt = t(6000);
    } else if (s.id === "screenshare-bypass") {
      setPhase("idle");
      timers.push(setTimeout(() => setPhase("prompt"), t(800)));
      timers.push(setTimeout(() => setPhase("action"), t(2500)));
      endAt = t(5000);
    } else if (s.id === "netacad-autosolve") {
      setPhase("idle");
      timers.push(setTimeout(() => setPhase("move"), t(500)));
      timers.push(setTimeout(() => setPhase("hover"), t(1200)));
      timers.push(setTimeout(() => setPhase("click"), t(1800)));
      timers.push(setTimeout(() => setPhase("action"), t(2500)));
      endAt = t(5200);
    } else {
      setPhase("idle");
      timers.push(setTimeout(() => setPhase("pressed"), t(1000)));
      timers.push(setTimeout(() => setPhase("action"), t(1600)));
      endAt = s.id === "alt-shift-t"
        ? t(1600) + CODE_ANSWER.length * TYPE_SPEED + t(1500)
        : t(4000);
    }

    timers.push(setTimeout(() => setSceneIdx((idx + 1) % SCENES.length), endAt + SCENE_END_HOLD));
    timerRef.current = timers;
  }, []);

  useEffect(() => {
    runTimeline(sceneIdx);
    return () => { if (timerRef.current) timerRef.current.forEach(clearTimeout); };
  }, [sceneIdx, runTimeline]);

  const pressed = phase === "pressed" || phase === "action";

  return { sceneIdx, phase, pressed, scene, setSceneIdx };
}

/* ═══════════════════════════════════════════════════════════════════════════
   RIGHT STAGE (REUSABLE)
   ═══════════════════════════════════════════════════════════════════════════ */
export function HeroStage({ includeFonts = true, className, style, showSlider = true }) {
  const { sceneIdx, phase, pressed, scene, setSceneIdx } = useHeroTimeline();

  return (
    <div
      className={className}
      style={{
        width: "100%",
        maxWidth: 600,
        minWidth: 280,
        ...style,
      }}
    >
      {includeFonts && <style>{FONTS}</style>}

      {/* Topic + Key label above stage */}
      <div style={{
        marginBottom: 16, minHeight: 44,
        display: "flex", alignItems: "center", justifyContent: "space-between"
      }}>
        <AnimatePresence mode="wait">
          <motion.div key={scene.id}
            initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 8 }}
            style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {/* Index */}
            <span style={{
              fontFamily: "'IBM Plex Mono',monospace", fontSize: 11,
              color: T.dim, letterSpacing: "0.06em"
            }}>
              {String(sceneIdx + 1).padStart(2, "0")}
            </span>
            {/* Separator */}
            {/* <span style={{ width: 20, height: 1, background: T.border }} /> */}
            {/* Topic */}
            <span style={{
              fontSize: 13, color: T.muted,
              fontFamily: "'Instrument Sans',sans-serif"
            }}>
              {scene.topic}
            </span>
          </motion.div>
        </AnimatePresence>

        {/* Key combo */}
        <AnimatePresence mode="wait">
          {scene.keys && (
            <motion.div key={scene.id}
              initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }}>
              {scene.control === "mouse"
                ? <MouseKey pressed={pressed || phase === "hover" || phase === "click"} />
                : <KeyCombo keys={scene.keys} pressed={pressed} />}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Stage card */}
      <div style={{ position: "relative" }}>
        {/* Outer glow ring */}
        <motion.div
          animate={{ opacity: pressed ? 0.6 : 0.2 }}
          style={{
            position: "absolute", inset: -1,
            borderRadius: 22,
            background: "transparent",
            boxShadow: `0 0 0 1px ${T.limeGlow}, 0 32px 80px rgba(0,0,0,0.6)`,
            pointerEvents: "none",
          }}
        />

        <motion.div
          initial={{ rotateY: 4, rotateX: 1.5 }}
          animate={{ rotateY: 0, rotateX: 0 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          style={{
            width: "100%",
            aspectRatio: "4/3",
            background: `linear-gradient(145deg, ${T.panel} 0%, #08080D 100%)`,
            border: `1px solid ${T.borderMid}`,
            borderRadius: 20,
            padding: "28px 28px 24px",
            boxShadow: `
              0 2px 0 rgba(255,255,255,0.04) inset,
              0 60px 120px -20px rgba(0,0,0,0.9),
              0 0 0 1px rgba(0,0,0,0.5)
            `,
            position: "relative",
            overflow: "hidden",
            transformStyle: "preserve-3d",
            boxSizing: "border-box",
          }}
        >
          {/* Top-right corner accent */}
          <div style={{
            position: "absolute", top: 0, right: 0, width: 120, height: 120,
            background: `radial-gradient(circle at top right, ${T.violetDim} 0%, transparent 60%)`,
            pointerEvents: "none",
          }} />
          {/* Bottom-left corner accent */}
          <div style={{
            position: "absolute", bottom: 0, left: 0, width: 100, height: 100,
            background: `radial-gradient(circle at bottom left, ${T.limeDim} 0%, transparent 60%)`,
            pointerEvents: "none",
          }} />

          <AnimatePresence mode="wait">
            <motion.div key={scene.id}
              initial={{ opacity: 0, y: 10, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 1.02 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              style={{ height: "100%", position: "relative", zIndex: 2 }}
            >
              {scene.id === "ctrl-q" && <SceneAskAI phase={phase} />}
              {scene.id === "alt-shift-a" && <SceneInstantSolve phase={phase} />}
              {scene.id === "alt-shift-t" && <SceneAutoType phase={phase} />}
              {scene.id === "alt-shift-q" && <SceneBatchSolve phase={phase} />}
              {scene.id === "right-click-solve" && <SceneFullPage phase={phase} />}
              {scene.id === "netacad-autosolve" && <SceneNetAcadAutosolve phase={phase} />}
              {scene.id === "screenshare-bypass" && <SceneStealth phase={phase} />}
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </div>

      

      {showSlider && (
        <div style={{
          marginTop: 24,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          position: "relative",
          zIndex: 10,
          padding: "6px 10px",
          borderRadius: 999,
        }}>
          {SCENES.map((s, i) => (
            <motion.button
              key={s.id}
              onClick={() => setSceneIdx(i)}
              style={{
                height: 4,
                borderRadius: 999,
                border: "none",
                cursor: "pointer",
                padding: 0,
                background: i === sceneIdx ? T.lime : T.sliderco,
                boxShadow: i === sceneIdx ? `0 0 10px ${T.limeGlow}` : "none",
                transition: "width 0.35s cubic-bezier(0.22,1,0.36,1), background 0.35s",
              }}
              animate={{ width: i === sceneIdx ? 44 : 14 }}
              aria-label={`Go to scene ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN HERO
   ═══════════════════════════════════════════════════════════════════════════ */
export default function Demo() {
  const { sceneIdx, phase, pressed, scene, setSceneIdx } = useHeroTimeline();

  return (
    <>
      {/* Font injection */}
      <style>{FONTS}</style>

      <section style={{
        position: "relative", width: "100%", minHeight: "100vh",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "72px 48px",
        background: T.bg,
        overflow: "hidden",
        fontFamily: "'Instrument Sans','Helvetica Neue',sans-serif",
        color: T.text,
        boxSizing: "border-box",
      }}>

        {/* ── Background depth layers ── */}
        {/* Fine grid */}
        <div style={{
          position: "absolute", inset: 0, opacity: 0.35,
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)
          `,
          backgroundSize: "44px 44px",
          maskImage: "radial-gradient(ellipse 70% 70% at 50% 40%, black 30%, transparent 80%)",
        }} />
        {/* Violet ambient */}
        <div style={{
          position: "absolute", top: "15%", left: "5%",
          width: "45vw", height: "45vw",
          background: "radial-gradient(circle, rgba(124,110,250,0.12) 0%, transparent 65%)",
          filter: "blur(80px)", pointerEvents: "none",
        }} />
        {/* Lime ambient */}
        <div style={{
          position: "absolute", bottom: "8%", right: "5%",
          width: "40vw", height: "40vw",
          background: "radial-gradient(circle, rgba(203,255,77,0.06) 0%, transparent 65%)",
          filter: "blur(90px)", pointerEvents: "none",
        }} />
        {/* Horizontal separator line */}
        <div style={{
          position: "absolute", top: "50%", left: "48px", right: "48px", height: 1,
          background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.04) 30%, rgba(255,255,255,0.04) 70%, transparent)",
          opacity: 0.5, pointerEvents: "none",
        }} />

        {/* ── Main grid ── */}
        <div style={{
          position: "relative", zIndex: 10,
          width: "100%", maxWidth: 1260,
          display: "grid",
          gridTemplateColumns: "1fr 1.15fr",
          gap: "80px",
          alignItems: "center",
        }}>

          {/* ══════════════════════════════════════
              LEFT — COPY
          ══════════════════════════════════════ */}
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>

            {/* Eyebrow pill */}
            <div style={{ marginBottom: 28 }}>
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                padding: "6px 14px",
                background: T.violetDim,
                border: `1px solid rgba(124,110,250,0.28)`,
                borderRadius: 100,
                fontSize: 11, fontWeight: 600, color: T.violet,
                fontFamily: "'IBM Plex Mono',monospace",
                letterSpacing: "0.1em", textTransform: "uppercase",
              }}>
                <span style={{
                  width: 5, height: 5, borderRadius: "50%", background: T.violet,
                  boxShadow: `0 0 6px ${T.violet}`
                }} />
                AI-Powered Test Suite
              </span>
            </div>

            {/* Headline */}
            <h1 style={{
              margin: "0 0 24px",
              fontFamily: "'Syne',sans-serif",
              fontSize: "clamp(42px,4.8vw,68px)",
              fontWeight: 800,
              lineHeight: 1.0,
              letterSpacing: "-0.03em",
              color: "#FAFAFA",
            }}>
              Ace every<br />
              <span style={{
                background: `linear-gradient(110deg, ${T.lime} 0%, #90EE50 50%, #CBFF4D 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}>exam</span>
              {" "}you take.
            </h1>

            {/* Sub */}
            <p style={{
              margin: "0 0 36px",
              fontSize: 15.5,
              lineHeight: 1.65,
              color: T.muted,
              maxWidth: 420,
              fontFamily: "'Instrument Sans',sans-serif",
            }}>
              NeoPass is a browser extension and AI assistant that solves, automarks, and bypasses proctoring — all with a single keystroke.
            </p>

            {/* Social proof */}
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 36 }}>
              <div style={{ display: "flex" }}>
                {["#7C6EFA", "#34D399", "#CBFF4D", "#F87171", "#60A5FA"].map((c, i) => (
                  <div key={i} style={{
                    width: 28, height: 28, borderRadius: "50%",
                    background: c, border: `2px solid ${T.bg}`,
                    marginLeft: i > 0 ? -9 : 0, zIndex: 5 - i, flexShrink: 0,
                  }} />
                ))}
              </div>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: "#FBBF24", fontSize: 13, letterSpacing: 2 }}>★★★★★</span>
                  <span style={{ fontWeight: 700, fontSize: 13, color: T.text }}>25.2K</span>
                </div>
                <span style={{ fontSize: 11.5, color: T.dim }}>Trusted by students worldwide</span>
              </div>
            </div>

            {/* CTA Row */}
            <div style={{ display: "flex", gap: 10, marginBottom: 40 }}>
              <button style={{
                flex: 1, padding: "13px 22px",
                background: T.lime, color: "#000",
                border: "none", borderRadius: 10, cursor: "pointer",
                fontSize: 13.5, fontWeight: 700,
                fontFamily: "'Syne',sans-serif",
                letterSpacing: "-0.01em",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                boxShadow: `0 8px 24px ${T.limeGlow}`,
                transition: "transform 0.15s, box-shadow 0.15s",
              }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow = `0 12px 32px ${T.limeGlow}`;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = "";
                  e.currentTarget.style.boxShadow = `0 8px 24px ${T.limeGlow}`;
                }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="8.5" cy="7" r="4" />
                  <line x1="20" y1="8" x2="20" y2="14" />
                  <line x1="23" y1="11" x2="17" y2="11" />
                </svg>
                Create Pro Account
              </button>
              <button style={{
                padding: "13px 22px",
                background: "transparent", color: T.text,
                border: `1px solid ${T.borderHi}`, borderRadius: 10, cursor: "pointer",
                fontSize: 13.5, fontWeight: 600,
                fontFamily: "'Instrument Sans',sans-serif",
                display: "flex", alignItems: "center", gap: 8,
                transition: "border-color 0.15s, background 0.15s",
              }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = T.surfaceHi;
                  e.currentTarget.style.borderColor = T.violet;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.borderColor = T.borderHi;
                }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                  <polyline points="10 17 15 12 10 7" />
                  <line x1="15" y1="12" x2="3" y2="12" />
                </svg>
                Sign In
              </button>
              <button style={{
                padding: "13px 16px",
                background: "transparent", color: T.muted,
                border: `1px solid ${T.borderHi}`, borderRadius: 10, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "border-color 0.15s, color 0.15s",
              }}
                onMouseEnter={e => {
                  e.currentTarget.style.color = T.text;
                  e.currentTarget.style.borderColor = T.violet;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.color = T.muted;
                  e.currentTarget.style.borderColor = T.borderHi;
                }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              </button>
            </div>

            {/* Feature chips */}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {[
                { icon: "◈", label: "AI Solver", color: T.violet },
                { icon: "⬡", label: "NeoExamShield", color: "#60A5FA" },
                { icon: "◎", label: "NeoBrowser", color: T.emerald },
              ].map(f => (
                <div key={f.label} style={{
                  display: "flex", alignItems: "center", gap: 7,
                  padding: "6px 12px",
                  background: T.surfaceHi,
                  border: `1px solid ${T.border}`,
                  borderRadius: 8,
                  fontSize: 12, color: T.muted,
                  fontFamily: "'Instrument Sans',sans-serif",
                }}>
                  <span style={{ color: f.color, fontSize: 13 }}>{f.icon}</span>
                  {f.label}
                </div>
              ))}
            </div>

            {/* Scene nav dots */}
            <div style={{ display: "flex", gap: 8, marginTop: 48, alignItems: "center" }}>
              {SCENES.map((s, i) => (
                <motion.button key={s.id} onClick={() => setSceneIdx(i)}
                  style={{
                    height: 3, borderRadius: 2, border: "none", cursor: "pointer", padding: 0,
                    background: i === sceneIdx ? T.lime : T.surfaceHi,
                    transition: "width 0.35s cubic-bezier(0.22,1,0.36,1), background 0.35s",
                  }}
                  animate={{ width: i === sceneIdx ? 36 : 12 }}
                />
              ))}
              <span style={{
                marginLeft: 4, fontSize: 11, color: T.dim,
                fontFamily: "'IBM Plex Mono',monospace", letterSpacing: "0.04em"
              }}>
                {String(sceneIdx + 1).padStart(2, "0")}/{String(SCENES.length).padStart(2, "0")}
              </span>
            </div>
          </div>

          {/* ══════════════════════════════════════
              RIGHT — STAGE
          ══════════════════════════════════════ */}
          <div style={{ position: "relative" }}>

            {/* Topic + Key label above stage */}
            <div style={{
              marginBottom: 16, minHeight: 44,
              display: "flex", alignItems: "center", justifyContent: "space-between"
            }}>
              <AnimatePresence mode="wait">
                <motion.div key={scene.id}
                  initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 8 }}
                  style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  {/* Index */}
                  <span style={{
                    fontFamily: "'IBM Plex Mono',monospace", fontSize: 11,
                    color: T.dim, letterSpacing: "0.06em"
                  }}>
                    {String(sceneIdx + 1).padStart(2, "0")}
                  </span>
                  {/* Separator */}
                  <span style={{ width: 20, height: 1, background: T.border }} />
                  {/* Topic */}
                  <span style={{
                    fontSize: 13, color: T.muted,
                    fontFamily: "'Instrument Sans',sans-serif"
                  }}>
                    {scene.topic}
                  </span>
                </motion.div>
              </AnimatePresence>

              {/* Key combo */}
              <AnimatePresence mode="wait">
                {scene.keys && (
                  <motion.div key={scene.id}
                    initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }}>
                    {scene.control === "mouse"
                      ? <MouseKey pressed={pressed || phase === "hover" || phase === "click"} />
                      : <KeyCombo keys={scene.keys} pressed={pressed} />}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Stage card */}
            <div style={{ position: "relative" }}>
              {/* Outer glow ring */}
              <motion.div
                animate={{ opacity: pressed ? 0.6 : 0.2 }}
                style={{
                  position: "absolute", inset: -1,
                  borderRadius: 22,
                  background: "transparent",
                  boxShadow: `0 0 0 1px ${T.limeGlow}, 0 32px 80px rgba(0,0,0,0.6)`,
                  pointerEvents: "none",
                }}
              />

              <motion.div
                initial={{ rotateY: 4, rotateX: 1.5 }}
                animate={{ rotateY: 0, rotateX: 0 }}
                transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                style={{
                  width: "100%",
                  aspectRatio: "4/3",
                  background: `linear-gradient(145deg, ${T.panel} 0%, #08080D 100%)`,
                  border: `1px solid ${T.borderMid}`,
                  borderRadius: 20,
                  padding: "28px 28px 24px",
                  boxShadow: `
                    0 2px 0 rgba(255,255,255,0.04) inset,
                    0 60px 120px -20px rgba(0,0,0,0.9),
                    0 0 0 1px rgba(0,0,0,0.5)
                  `,
                  position: "relative",
                  overflow: "hidden",
                  transformStyle: "preserve-3d",
                  boxSizing: "border-box",
                }}
              >
                {/* Top-right corner accent */}
                <div style={{
                  position: "absolute", top: 0, right: 0, width: 120, height: 120,
                  background: `radial-gradient(circle at top right, ${T.violetDim} 0%, transparent 60%)`,
                  pointerEvents: "none",
                }} />
                {/* Bottom-left corner accent */}
                <div style={{
                  position: "absolute", bottom: 0, left: 0, width: 100, height: 100,
                  background: `radial-gradient(circle at bottom left, ${T.limeDim} 0%, transparent 60%)`,
                  pointerEvents: "none",
                }} />

                <AnimatePresence mode="wait">
                  <motion.div key={scene.id}
                    initial={{ opacity: 0, y: 10, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 1.02 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    style={{ height: "100%", position: "relative", zIndex: 2 }}
                  >
                    {scene.id === "ctrl-q" && <SceneAskAI phase={phase} />}
                    {scene.id === "alt-shift-a" && <SceneInstantSolve phase={phase} />}
                    {scene.id === "alt-shift-t" && <SceneAutoType phase={phase} />}
                    {scene.id === "alt-shift-q" && <SceneBatchSolve phase={phase} />}
                    {scene.id === "right-click-solve" && <SceneFullPage phase={phase} />}
                    {scene.id === "netacad-autosolve" && <SceneNetAcadAutosolve phase={phase} />}
                    {scene.id === "screenshare-bypass" && <SceneStealth phase={phase} />}
                  </motion.div>
                </AnimatePresence>
              </motion.div>
            </div>

            {/* Below-stage status bar */}
            <div style={{
              marginTop: 14,
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "10px 16px",
              background: T.surface,
              border: `1px solid ${T.border}`,
              borderRadius: 10,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <motion.div
                  animate={{
                    background: pressed ? T.lime : T.emerald,
                    boxShadow: pressed ? `0 0 8px ${T.lime}` : `0 0 6px ${T.emerald}`
                  }}
                  style={{ width: 6, height: 6, borderRadius: "50%", flexShrink: 0 }}
                />
                <span style={{
                  fontSize: 11.5, color: T.muted,
                  fontFamily: "'Instrument Sans',sans-serif"
                }}>
                  {pressed ? "NeoPass active" : "NeoPass ready"}
                </span>
              </div>
              <div style={{ display: "flex", gap: 14 }}>
                {["Extension v3.2", "Chrome 124", "Stealth ON"].map((label, i) => (
                  <span key={i} style={{
                    fontSize: 10.5, color: T.dim,
                    fontFamily: "'IBM Plex Mono',monospace", letterSpacing: "0.04em"
                  }}>
                    {label}
                  </span>
                ))}
              </div>
            </div>
          </div>

        </div>
      </section>
    </>
  );
}